import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Loader2, Plus, FolderTree, BarChart3, Sparkles, History, Undo2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import RecruiterLayout from '@/components/RecruiterLayout';
import SkillTaxonomyTree from '@/components/skills/SkillTaxonomyTree';
import SkillFormDialog from '@/components/skills/SkillFormDialog';
import AISkillSuggestions from '@/components/skills/AISkillSuggestions';
import AITaxonomyOrganizer from '@/components/skills/AITaxonomyOrganizer';
import SkillCompanyReport from '@/components/skills/SkillCompanyReport';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermissions } from '@/hooks/usePermissions';

interface Skill {
  id: string;
  name: string;
  type: string | null;
  parent_id: string | null;
  is_bucket: boolean;
  position: number;
  description: string | null;
}

interface MergeEvent {
  id: string;
  primary_skill_id: string;
  merged_skill_id: string;
  merged_skill_name: string;
  merged_at: string;
  notes: string | null;
  skills: { name: string } | null;
}

const SkillsManagement = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [mergeEvents, setMergeEvents] = useState<MergeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [parentIdForNew, setParentIdForNew] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);
  const { toast } = useToast();
  const { can, loading: permissionsLoading } = usePermissions();

  useEffect(() => {
    fetchSkills();
    fetchMergeEvents();
  }, []);

  const fetchSkills = async () => {
    try {
      const { data, error } = await supabase
        .from('skills')
        .select('*')
        .order('position')
        .order('name');
      if (error) throw error;
      setSkills((data || []).map(s => ({
        ...s,
        is_bucket: s.is_bucket || false,
        position: s.position || 0,
        description: s.description || null
      })));
    } catch (error) {
      console.error('Error fetching skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMergeEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('skill_merge_events')
        .select('*, skills(name)')
        .order('merged_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      setMergeEvents((data as any) || []);
    } catch (error) {
      console.error('Error fetching merge events:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this skill? Child skills will become orphaned.')) return;
    try {
      const { error } = await supabase.from('skills').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Skill deleted successfully' });
      fetchSkills();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSubmit = async (data: any, id?: string) => {
    try {
      const payload = {
        name: data.name,
        type: data.is_bucket ? null : data.type,
        parent_id: data.parent_id,
        is_bucket: data.is_bucket,
        description: data.description || null,
      };

      if (id) {
        const { error } = await supabase.from('skills').update(payload).eq('id', id);
        if (error) throw error;
        toast({ title: 'Skill updated successfully' });
      } else {
        const { error } = await supabase.from('skills').insert([payload]);
        if (error) throw error;
        toast({ title: 'Skill created successfully' });
      }
      fetchSkills();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleEdit = (skill: Skill) => {
    setSelectedSkill(skill);
    setParentIdForNew(null);
    setFormOpen(true);
  };

  const handleAddChild = (parentId: string) => {
    setSelectedSkill(null);
    setParentIdForNew(parentId);
    setFormOpen(true);
  };

  const handleAddSkillsFromAI = async (newSkills: { name: string; type: string }[]) => {
    try {
      const { error } = await supabase.from('skills').insert(newSkills);
      if (error) throw error;
      fetchSkills();
    } catch (error: any) {
      toast({ title: 'Error adding skills', description: error.message, variant: 'destructive' });
    }
  };

  const handleApplyTaxonomy = async (buckets: { name: string; skills: string[] }[]) => {
    try {
      for (const bucket of buckets) {
        // Check if bucket already exists (either as is_bucket or regular skill with same name)
        let existingSkill = skills.find(s => s.name.toLowerCase() === bucket.name.toLowerCase());
        let bucketId: string;
        
        if (existingSkill) {
          // If it exists but isn't a bucket, convert it to a bucket
          if (!existingSkill.is_bucket) {
            await supabase
              .from('skills')
              .update({ is_bucket: true })
              .eq('id', existingSkill.id);
          }
          bucketId = existingSkill.id;
        } else {
          // Create new bucket
          const { data: newBucket, error: insertError } = await supabase
            .from('skills')
            .insert({ name: bucket.name, is_bucket: true })
            .select('id')
            .single();
          
          if (insertError) throw insertError;
          bucketId = newBucket.id;
        }

        // Update skills to be under this bucket
        for (const skillName of bucket.skills) {
          const skill = skills.find(s => s.name.toLowerCase() === skillName.toLowerCase() && s.id !== bucketId);
          if (skill && !skill.parent_id) {
            await supabase
              .from('skills')
              .update({ parent_id: bucketId })
              .eq('id', skill.id);
          }
        }
      }
      
      toast({ title: 'Taxonomy applied successfully' });
      fetchSkills();
    } catch (error: any) {
      toast({ title: 'Error applying taxonomy', description: error.message, variant: 'destructive' });
    }
  };


  const handleUndoMerge = async (event: MergeEvent) => {
    setUndoing(event.id);
    
    try {
      // Recreate the merged skill
      const { data: newSkill, error: insertError } = await supabase
        .from('skills')
        .insert({
          name: event.merged_skill_name,
          type: 'technical', // Default type since we don't store the original
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Delete the merge event
      const { error: deleteError } = await supabase
        .from('skill_merge_events')
        .delete()
        .eq('id', event.id);

      if (deleteError) throw deleteError;

      toast({ 
        title: 'Merge undone',
        description: `"${event.merged_skill_name}" has been recreated. Note: Previous skill associations were not restored.`
      });
      
      fetchSkills();
      fetchMergeEvents();
    } catch (error: any) {
      toast({ 
        title: 'Failed to undo merge', 
        description: error.message, 
        variant: 'destructive' 
      });
    } finally {
      setUndoing(null);
    }
  };

  const handleMoveSkill = async (skillId: string, newParentId: string | null) => {
    try {
      const { error } = await supabase
        .from('skills')
        .update({ parent_id: newParentId })
        .eq('id', skillId);
      
      if (error) throw error;
      
      toast({ title: 'Skill moved successfully' });
      fetchSkills();
    } catch (error: any) {
      toast({ title: 'Failed to move skill', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateBucket = async (name: string, parentId: string | null) => {
    try {
      const { error } = await supabase
        .from('skills')
        .insert({
          name,
          is_bucket: true,
          parent_id: parentId,
          type: null,
        });
      
      if (error) throw error;
      
      toast({ title: 'Bucket created successfully' });
      fetchSkills();
    } catch (error: any) {
      toast({ title: 'Failed to create bucket', description: error.message, variant: 'destructive' });
    }
  };

  const buckets = skills.filter(s => s.is_bucket);

  const filteredSkills = skills.filter(skill =>
    skill.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type: string | null) => {
    switch (type) {
      case 'technical': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'framework': return 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400';
      case 'tool': return 'bg-purple-500/10 text-purple-700 dark:text-purple-400';
      case 'soft': return 'bg-pink-500/10 text-pink-700 dark:text-pink-400';
      case 'language': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'certification': return 'bg-orange-500/10 text-orange-700 dark:text-orange-400';
      case 'product': return 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400';
      default: return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    }
  };

  return (
    <RecruiterLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Skills Taxonomy</h1>
            <p className="text-muted-foreground">
              Manage hierarchical skill categories and AI-powered skill suggestions
            </p>
          </div>
          {can.create('skills') && (
            <Button onClick={() => { setSelectedSkill(null); setParentIdForNew(null); setFormOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Skill
            </Button>
          )}
        </div>

        <Tabs defaultValue="taxonomy" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="taxonomy" className="flex items-center gap-2">
              <FolderTree className="h-4 w-4" />
              <span className="hidden sm:inline">Taxonomy</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">AI Tools</span>
            </TabsTrigger>
            <TabsTrigger value="report" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Company Report</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
          </TabsList>

          {/* Taxonomy Tab - AIR-154, AIR-160 */}
          <TabsContent value="taxonomy" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : searchTerm ? (
              <Card className="p-4">
                <div className="flex flex-wrap gap-2">
                  {filteredSkills.map(skill => (
                    <Badge
                      key={skill.id}
                      variant="secondary"
                      className={`cursor-pointer ${getTypeColor(skill.type)}`}
                      onClick={() => handleEdit(skill)}
                    >
                      {skill.name}
                    </Badge>
                  ))}
                  {filteredSkills.length === 0 && (
                    <p className="text-muted-foreground">No skills match your search</p>
                  )}
                </div>
              </Card>
            ) : (
              <Card className="p-4">
                <SkillTaxonomyTree
                  skills={skills}
                  onEdit={can.update('skills') ? handleEdit : undefined}
                  onDelete={can.delete('skills') ? handleDelete : undefined}
                  onAddChild={can.create('skills') ? handleAddChild : undefined}
                  onMoveSkill={can.update('skills') ? handleMoveSkill : undefined}
                  onCreateBucket={can.create('skills') ? handleCreateBucket : undefined}
                />
              </Card>
            )}

            <div className="text-sm text-muted-foreground">
              {skills.length} total skills • {buckets.length} buckets
            </div>
          </TabsContent>

          {/* AI Tools Tab - AIR-155, AIR-166, AIR-167, AIR-168 */}
          <TabsContent value="ai" className="space-y-6">
            <AITaxonomyOrganizer
              existingSkills={skills}
              existingBuckets={buckets}
              onApplyTaxonomy={handleApplyTaxonomy}
            />
            <AISkillSuggestions
              existingSkills={skills.map(s => s.name)}
              onAddSkills={handleAddSkillsFromAI}
            />
          </TabsContent>

          {/* Company Report Tab - AIR-163 */}
          <TabsContent value="report">
            <SkillCompanyReport />
          </TabsContent>

          {/* History Tab - AIR-165 */}
          <TabsContent value="history" className="space-y-4">
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <History className="h-5 w-5" />
                Merge History
              </h3>
              {mergeEvents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No merge events recorded yet
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Merged Skill</TableHead>
                      <TableHead>Merged Into</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-20">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mergeEvents.map(event => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.merged_skill_name}</TableCell>
                        <TableCell>{event.skills?.name || 'Unknown'}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(event.merged_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUndoMerge(event)}
                            disabled={undoing === event.id}
                            className="h-8 px-2"
                          >
                            {undoing === event.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Undo2 className="h-4 w-4" />
                            )}
                            <span className="ml-1 hidden sm:inline">Undo</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <SkillFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          skill={selectedSkill}
          parentId={parentIdForNew}
          buckets={buckets}
          onSubmit={handleSubmit}
        />
      </div>
    </RecruiterLayout>
  );
};

export default SkillsManagement;